// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiParameter,
    get,
    HasPagingParameters,
    patch,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";

const getUsersUri = "/api/synd/retailers/users";
const getRetailersUri = "/api/synd/retailers";

export interface RetailerUserData {
    uri: string | null;
    id: string;
    retailerName: string;
    retailerId: string;
    firstName: string;
    lastName: string;
    customerName: string;
    email: string;
    phone: string;
    city: string;
    stateId: string;
    stateAbbreviation: string;
    stateName: string;
    properties: { [key: string]: string };
}

export interface UsersModel {
    users: RetailerUserData[];
    pagination: PaginationModel | null;
}

export interface RetailUsersApiParameter extends ApiParameter, HasPagingParameters {
    search?: string;
    sort?: string;
}

export interface GetRetailManagementApiParameter extends ApiParameter {
    filterToWholesaler: boolean;
}
// =========================================== Get retailers users =========================================== //

export async function getRetailUsers(parameter: RetailUsersApiParameter): Promise<ServiceResult<UsersModel>> {
    try {
        const res = await get<UsersModel>(getUsersUri, parameter);
        return {
            result: res,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

// =========================================== Get retailers =========================================== //

export interface RetailersDataModel {
    retailers: { id: string; name: string }[];
    uri: string;
    properties: { [key: string]: string };
}

export async function getRetailers(query: GetRetailManagementApiParameter): Promise<ServiceResult<RetailersDataModel>> {
    try {
        const res = await get<RetailersDataModel>(getRetailersUri, query);
        return {
            result: res,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

// =========================================== Create/Edit Retailer User ======================================== //
export interface CreateRetailUserApiParameter extends ApiParameter {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    retailerName: string;
    city: string;
    stateId: string;
}
export interface EditRetailUserApiParameter extends ApiParameter {
    firstName?: string;
    lastName?: string;
    phone?: string;
    id?: string;
}

export async function createRetailers(
    parameter: CreateRetailUserApiParameter,
): Promise<ServiceResult<RetailerUserData>> {
    try {
        const res = await post<CreateRetailUserApiParameter, RetailerUserData>(getUsersUri, parameter);
        return {
            result: res,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function updateRetailers(parameter: EditRetailUserApiParameter): Promise<ServiceResult<RetailerUserData>> {
    try {
        const { id, firstName, lastName, phone } = parameter;
        const res = await patch<any>(`${getUsersUri}/${id}`, { firstName, lastName, phone });
        return {
            result: res,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function deactivateRetailer(parameter: { id: string }): Promise<ServiceResult<any>> {
    try {
        const { id } = parameter;
        const res = await patch<any>(`${getUsersUri}/${id}/deactivate`, {});
        return {
            result: res,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}
